import React, { useEffect, useRef, useState, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Phone, PhoneOff, Mic, MicOff, ShieldAlert, Headphones, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CallOutcomeDialog, OUTCOME_LABELS } from "./CallOutcomeDialog";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const CallHistory = ({ calls }) => (
  <div className="mt-4 space-y-2" data-testid="contact-call-history">
    {calls.slice(0, 5).map((c) => (
      <div key={c.id} className="flex justify-between text-xs text-muted-foreground">
        <span>{c.direction === "inbound" ? "Inbound" : "Outbound"} · {new Date(c.created_at).toLocaleDateString()}</span>
        <span className={c.status === "missed" ? "text-destructive" : ""}>
          {c.outcome ? OUTCOME_LABELS[c.outcome] : c.status}{c.duration ? ` · ${fmt(c.duration)}` : ""}
        </span>
      </div>
    ))}
  </div>
);

export const CallPanel = ({ contact, onLogged }) => {
  const [pf, setPf] = useState(null);
  const [calls, setCalls] = useState([]);
  const [state, setState] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [secs, setSecs] = useState(0);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const device = useRef(null);
  const call = useRef(null);

  const load = useCallback(() => {
    api.get(`/calling/preflight/${contact.id}`).then(({ data }) => setPf(data)).catch(() => {});
    api.get(`/calling/calls?sub_account_id=${contact.sub_account_id}&contact_id=${contact.id}`).then(({ data }) => setCalls(data)).catch(() => {});
  }, [contact.id, contact.sub_account_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => device.current?.destroy(), []);
  useEffect(() => {
    if (state !== "live") return;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  const dial = async () => {
    setState("connecting"); setSecs(0); setMuted(false);
    try {
      const { data: intent } = await api.post("/calling/intents", { contact_id: contact.id });
      if (!device.current) {
        const { data } = await api.get("/calling/token");
        device.current = new Device(data.token, { codecPreferences: ["opus", "pcmu"] });
        device.current.on("tokenWillExpire", async () => {
          const r = await api.get("/calling/token"); device.current.updateToken(r.data.token);
        });
      }
      const c = await device.current.connect({ params: { intent_id: intent.intent_id } });
      call.current = c;
      c.on("accept", () => setState("live"));
      const end = () => { setState("idle"); call.current = null; setOutcomeFor(intent.intent_id); };
      c.on("disconnect", end); c.on("cancel", end);
      c.on("error", (e) => { toast.error(e.message || "Call failed"); end(); });
    } catch (e) {
      setState("idle");
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const hangup = () => call.current?.disconnect();
  const toggleMute = () => { call.current?.mute(!muted); setMuted(!muted); };
  const setFlag = async (k, v) => { await api.put(`/calling/contacts/${contact.id}/flags`, { [k]: v }); load(); onLogged?.(); };

  if (!pf) return null;
  const busy = state !== "idle";
  return (
    <Card className="p-6 bg-card border-border/70" data-testid="call-panel">
      <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><Headphones className="w-4 h-4" /> Call</h3>
      {pf.from_number && <p className="text-xs text-muted-foreground mb-3" data-testid="call-from-number">Calling from {pf.from_number}{pf.local_time ? ` · lead time ${pf.local_time}` : ""}</p>}
      {!pf.allowed && !busy && (
        <div className="mb-3 space-y-1.5" data-testid="call-blocked-reasons">
          {pf.reasons.map((r) => <p key={r} className="text-xs text-amber-400 flex gap-1.5"><ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />{r}</p>)}
        </div>
      )}
      {pf.record_calls && <p className="text-xs text-muted-foreground mb-3">This call is recorded — both sides hear a recording notice.</p>}
      {busy ? (
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono flex-1" data-testid="call-status">{state === "live" ? fmt(secs) : <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Ringing…</span>}</span>
          <Button size="sm" variant="secondary" onClick={toggleMute} data-testid="call-mute-button">{muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
          <Button size="sm" variant="destructive" onClick={hangup} data-testid="call-hangup-button"><PhoneOff className="w-4 h-4" /></Button>
        </div>
      ) : (
        <Button className="w-full gap-2" disabled={!pf.allowed} onClick={dial} data-testid="call-dial-button"><Phone className="w-4 h-4" /> Call {pf.to || ""}</Button>
      )}
      <div className="mt-4 flex flex-col gap-2 text-sm">
        <label className="flex items-center justify-between"><span>Do not call</span><Switch checked={!!contact.dnc} onCheckedChange={(v) => setFlag("dnc", v)} data-testid="contact-dnc-switch" /></label>
        {pf.country === "GB" && <label className="flex items-center justify-between"><span>On UK TPS register</span><Switch checked={!!contact.tps} onCheckedChange={(v) => setFlag("tps", v)} data-testid="contact-tps-switch" /></label>}
      </div>
      <CallHistory calls={calls} />
      <CallOutcomeDialog open={!!outcomeFor} callId={outcomeFor} onDone={() => { setOutcomeFor(null); load(); onLogged?.(); }} />
    </Card>
  );
};
